import React, { useCallback, useMemo, useState } from "react";

import {
  Alert,
  Breakpoints,
  Calendar,
  Close,
  CTA,
  Direction,
  Divider,
  Internet,
  Phone,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
  useIsMobile,
} from "@vw-marketing/us-components";

import { applyTextTransformations } from "../../../../shared/utils/applyTextTransformations";
import { Dealer, DealersList } from "../../../../shared/typings/main";
import { getOpenSchedule } from "../../../../shared/utils/dealer-utils";
import {
  onVWBasicDealerSelectClickEvent,
  onVWBasicDealerCardButtonClickEvent
} from "../../../../shared/tagging/tagging-helpers";
import {
  StyledBottomSectionContainer,
  StyledCTAsContainer,
  StyledDealerCardContainer,
  StyledDealerNameContainer,
  StyledDividerContainer,
  StyledEllipse,
  StyledErrorContainer,
  StyledErrorTitleContainer,
  StyledMiddleSectionContainer,
  StyledNameAndReviewsContainer,
  StyledScheduleInformation,
  StyledScheduleTextWrapper,
  StyledTopSectionContainer,
} from "./styles";
import { IconCta } from "../../../../shared/components/icon-cta/IconCta";

interface DealerCardProps {
  dealer: DealersList;
  onClose: () => void;
  onDealerSelect?: () => void;
  translationHandler: (key: string) => string;
  authService: any;
  trackingManager: any;
  localeCode: string;
  locale: string;
  fetchPreferredDealer: (vehicleId: string, vin: string, newDealerId?: string) => Promise<void>;
  userPreferredDealer?: Dealer;
  showToast: (message: any, appearance?: any, title?: string) => void;
  userPreferredVehicle: any;
  history: any;
  currentPathname: string;
}

export const DealerCard = ({
  dealer,
  onClose,
  onDealerSelect,
  translationHandler,
  authService,
  trackingManager,
  localeCode,
  locale,
  fetchPreferredDealer,
  userPreferredDealer,
  showToast,
  userPreferredVehicle,
  history,
  currentPathname,
}: DealerCardProps) => {
  const [isLoadingSetPreferredDealer, setIsLoadingSetPreferredDealer] = useState<boolean>(false);
  const [loadingSchedulingURL, setLoadingSchedulingURL] = useState<boolean>(false);

  const isCardMinWidth: boolean = useIsMobile(300);
  const isMobile: boolean = useIsMobile(Breakpoints.b960);

  const {
    address = {
      address1: "",
      city: "",
      state: "",
      zip: "",
    },
    dealerNames = [],
    departments = [],
    distance = 0,
    location = {
      latitude: 0,
      longitude: 0
    },
    phone = "",
    url = "",
  } = dealer ?? {} as Partial<DealersList>;

  const dealerAddress = useMemo(
    () => `${address.address1 || ''} ${address.city || ''}, ${address.state || ''} ${address.zip || ''}`,
    [address]
  );

  const dealerDistance = useMemo(
    () => distance?.toFixed(1) || '0',
    [distance]
  );

  const dealerName: string = dealerNames[0]?.name || dealerNames[1]?.name || '';
  const scheduleInformation = useMemo(
    () => getOpenSchedule(departments, locale),
    [departments, locale]
  );

  const isSetDealerCTADisabled: boolean = dealer?.id === userPreferredDealer?.id;

  // Determine if we're on dealer details page based on current path
  const isDealerDetailsPage = useMemo(() => {
    // Check if we're on the main manage-dealer page (not in a layer/modal)
    return currentPathname === '/manage-dealer';
  }, [currentPathname]);

  // Get tracking process data based on current page context
  const getTrackingProcessData = useCallback(() => {
    return {
      Name: 'Find a Dealer',
      StepName: isDealerDetailsPage ? 'Find a Dealer Map - Dealer Detail' : 'Find a Dealer Map',
      StepNumber: isDealerDetailsPage ? '3.1' : '3',
    };
  }, [isDealerDetailsPage]);

  // Track dealer card button clicks
  const trackDealerCardButtonClick = useCallback((linkName: string) => {
    try {
      onVWBasicDealerCardButtonClickEvent(
        trackingManager,
        getTrackingProcessData(),
        { name: linkName }
      );
    } catch (error) {
      console.warn('Error tracking dealer card button click:', error);
    }
  }, [trackingManager, getTrackingProcessData]);

  // Track dealer select event
  const trackDealerSelect = useCallback(() => {
    try {
      onVWBasicDealerSelectClickEvent(trackingManager, getTrackingProcessData());
    } catch (error) {
      console.warn('Error tracking dealer select event:', error);
    }
  }, [trackingManager, getTrackingProcessData]);

  const handleServiceOnClick = useCallback(async () => {
    // Track the button click
    trackDealerCardButtonClick('Service');

    if (!userPreferredVehicle) return;
    setLoadingSchedulingURL(true);
    try {
      const vehicleInfo = localeCode.toUpperCase().endsWith('-CA') ? userPreferredVehicle?.vehicleId : userPreferredVehicle?.vin;
      const { getServiceSchedulingUrl } =
        await authService.getServiceSchedulingUrl(vehicleInfo, dealer?.id, localeCode);

      if (!getServiceSchedulingUrl) return;

      window.open(getServiceSchedulingUrl.schedulingURL || '', '_blank');
    } catch (error) {
      console.error("There was an error fetching service URL on dealer card", error)
    } finally {
      setLoadingSchedulingURL(false)
    }
  }, [userPreferredVehicle, dealer?.id, authService, localeCode, trackDealerCardButtonClick]);

  const handleCallOnClick = useCallback(() => {
    // Track the button click
    trackDealerCardButtonClick('Call');

    window.open(`tel:${phone}`, '_blank');
  }, [phone, trackDealerCardButtonClick]);

  const handleWebSiteOnClick = useCallback(() => {
    // Track the button click
    trackDealerCardButtonClick('Website');

    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (!newWindow) {
      console.error("No url site to show")
    }
  }, [url, trackDealerCardButtonClick]);

  const handleRouteOnClick = useCallback(() => {
    // Track the button click
    trackDealerCardButtonClick('Route');

    // Prefer destination by address so the destination field is prefilled in Google Maps
    const destinationAddress = dealerAddress?.trim();
    const destination = destinationAddress && destinationAddress.length > 0
      ? encodeURIComponent(destinationAddress)
      : `${location.latitude},${location.longitude}`;

    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${destination}`,
      '_blank',
      'noopener,noreferrer'
    );
  }, [dealerAddress, location.latitude, location.longitude, trackDealerCardButtonClick]);

  const handleSetPreferredDealer = useCallback(async (dealerId: string) => {
    if (!dealerId) return;

    if (!userPreferredVehicle?.vehicleId || !userPreferredVehicle?.vin) {
      console.error('❌ No preferred vehicle found');
      return;
    }

    // Track dealer select event before processing
    trackDealerSelect();

    try {
      setIsLoadingSetPreferredDealer(true);

      await authService.saveUserPreferredDealerInfo(dealerId, localeCode);

      const successMessage: string | React.JSX.Element[] =
        applyTextTransformations(translationHandler('manageDealer.success.message').replace('{dealer_name}', dealerNames[0].name), [dealerNames[0].name]);

      // Pass dealerId to fetchPreferredDealer to ensure we get the updated dealer immediately
      await fetchPreferredDealer(
        userPreferredVehicle?.vehicleId || '',
        userPreferredVehicle?.vin || '',
        dealerId
      )

      showToast(successMessage, "Success");

      if (!currentPathname.includes('/manage-dealer')) {
        history.push('/manage-dealer');
      }
    } catch (error) {
      console.error("There was an error changing the user's preferred dealer", error)

      const errorMessage: string | React.JSX.Element[] =
        applyTextTransformations(translationHandler('manageDealer.error.message').replace('{dealer_name}', dealerNames[0].name), [dealerNames[0].name]);

      showToast(errorMessage, "Alert")
    } finally {
      onDealerSelect?.();
      setIsLoadingSetPreferredDealer(false);
    }
  }, [
    authService,
    localeCode,
    translationHandler,
    dealerNames,
    fetchPreferredDealer,
    userPreferredVehicle,
    showToast,
    currentPathname,
    history,
    onDealerSelect,
    trackDealerSelect,
  ]);

  if (!dealer) return (
    <StyledDealerCardContainer hasError={true}>
      <StyledErrorContainer>
        <StyledErrorTitleContainer>
          <Alert />
          <TextCustom
            tag={TextTag.p}
            appearance={TextAppearance.label200}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize20,
                fontWeight: TextWeight.regular,
                color: TextColor.primary,
                lineHeight: TextLineHeight.TextLineHeight24,
              },
            }}
          >
            {translationHandler('manageDealer.dealerCard.error.label')}
          </TextCustom>
        </StyledErrorTitleContainer>
        <TextCustom
          tag={TextTag.p}
          appearance={TextAppearance.copy200}
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize16,
              fontWeight: TextWeight.regular,
              color: TextColor.secondary,
              lineHeight: TextLineHeight.TextLineHeight24,
            },
          }}
        >
          {translationHandler('manageDealer.dealerCard.subError.label')}{" "}
          <CTA tag="a" emphasis="tertiary" aria-label="aria label for CTA">
            {translationHandler('manageDealer.dealerCard.tryAgain.CTA.label')}
          </CTA>
        </TextCustom>
      </StyledErrorContainer>
    </StyledDealerCardContainer>
  )

  return (
    <StyledDealerCardContainer hasError={false}>
      <StyledTopSectionContainer>
        <StyledNameAndReviewsContainer>
          <StyledDealerNameContainer>
            <TextCustom
              appearance={TextAppearance.copy200}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize20,
                  fontWeight: TextWeight.bold,
                  color: TextColor.primary,
                  lineHeight: TextLineHeight.TextLineHeight32,
                },
                [Breakpoints.b1600]: {
                  fontSize: TextSize.TextSize24,
                  fontWeight: TextWeight.bold,
                  color: TextColor.primary,
                  lineHeight: TextLineHeight.TextLineHeight36,
                },
              }}
            >
              {applyTextTransformations(dealerName, [dealerName])}
            </TextCustom>
            <CTA
              tag="button"
              emphasis="tertiary"
              aria-label="Close dealer card"
              icon={<Close />}
              onClick={onClose}
            />
          </StyledDealerNameContainer>
          {/* THIS IS NOT GOING TO BE IMPLEMENTED YET */}
          {/*  <StyledDealerReviewsContainer>
                <TextCustom
                  appearance={TextAppearance.copy200}
                  style={{
                    [Breakpoints.default]: {
                      fontSize: TextSize.TextSize14,
                      fontWeight: TextWeight.light,
                      color: TextColor.primary,
                      lineHeight: TextLineHeight.TextLineHeight24,
                    },
                    [Breakpoints.b1600]: {
                      fontSize: TextSize.TextSize16,
                    },
                  }}
                >
                  Here goes the Five stars FALoader?
                </TextCustom>
              </StyledDealerReviewsContainer> */}
        </StyledNameAndReviewsContainer>
        <StyledCTAsContainer isMobile={isMobile}>
          <IconCta
            aria-label="Service CTA"
            disabled={loadingSchedulingURL}
            icon={<Calendar variant={isMobile ? "small" : "default"} />}
            label={translationHandler('manageDealer.dealerCard.service.label')}
            onClick={handleServiceOnClick}
            emphasis="primary"
          />
          {isMobile && (
            <IconCta
              aria-label="Call CTA"
              icon={<Phone variant={isMobile ? "small" : "default"} />}
              label={translationHandler('manageDealer.dealerCard.call.label')}
              onClick={handleCallOnClick}
            />
          )}
          <IconCta
            aria-label="Website CTA"
            icon={<Internet variant={isMobile ? "small" : "default"} />}
            label={translationHandler('manageDealer.dealerCard.website.label')}
            onClick={handleWebSiteOnClick}
          />
          <IconCta
            aria-label="Route CTA"
            icon={<Direction variant={isMobile ? "small" : "default"} />}
            label={translationHandler('manageDealer.dealerCard.route.label')}
            onClick={handleRouteOnClick}
          />
        </StyledCTAsContainer>
      </StyledTopSectionContainer>
      <StyledDividerContainer>
        <Divider />
      </StyledDividerContainer>
      <StyledMiddleSectionContainer>
        <TextCustom
          appearance={TextAppearance.copy200}
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize16,
              fontWeight: TextWeight.regular,
              color: TextColor.primary,
              lineHeight: TextLineHeight.TextLineHeight24,
            },
          }}
        >
          {applyTextTransformations(dealerAddress, [dealerAddress])}
        </TextCustom>
        <TextCustom
          appearance={TextAppearance.copy200}
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize16,
              fontWeight: TextWeight.bold,
              color: TextColor.primary,
              lineHeight: TextLineHeight.TextLineHeight24,
            },
          }}
        >
          {dealerDistance} {translationHandler('manageDealer.dealerCard.miles.label')} {isCardMinWidth && translationHandler('manageDealer.dealerCard.fromYou.label')}
        </TextCustom>
      </StyledMiddleSectionContainer>
      <StyledDividerContainer>
        <Divider />
      </StyledDividerContainer>
      <StyledBottomSectionContainer>
        {scheduleInformation && (
          <StyledScheduleInformation>
            <StyledEllipse $status={scheduleInformation.closed ? 'closed' : 'open'} />
            <StyledScheduleTextWrapper $status={scheduleInformation.closed ? 'closed' : 'open'}>
              <TextCustom
                tag={TextTag.p}
                appearance={TextAppearance.copy200}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize16,
                    fontWeight: TextWeight.regular,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
              >
                {translationHandler(scheduleInformation.closed ?
                  'manageDealer.dealerCard.Closed.label' :
                  'manageDealer.dealerCard.Open.label')}{" "}
                <TextCustom
                  tag={TextTag.span}
                  appearance={TextAppearance.copy200}
                  style={{
                    [Breakpoints.default]: {
                      fontSize: TextSize.TextSize16,
                      fontWeight: TextWeight.regular,
                      color: TextColor.secondary,
                      lineHeight: TextLineHeight.TextLineHeight24,
                    },
                  }}
                >
                  ⋅ {translationHandler(scheduleInformation.labelKey)} {scheduleInformation.scheduleInfo}
                </TextCustom>
              </TextCustom>
            </StyledScheduleTextWrapper>
          </StyledScheduleInformation>
        )}
        <CTA
          tag="button"
          emphasis="primary"
          aria-label="Select Dealer CTA"
          onClick={() => handleSetPreferredDealer(dealer.id || '')}
          stretchContent
          disabled={isSetDealerCTADisabled || isLoadingSetPreferredDealer}
        >
          {translationHandler('manageDealer.map.selectDealer.label')}
        </CTA>
      </StyledBottomSectionContainer>
    </StyledDealerCardContainer>
  );
};